import React, { useEffect, useState } from "react";
import { supabase } from "./supabase/auth";
import { selectAll } from "./supabase/client";
import { useTranslation } from 'react-i18next';

interface SignupRequest {
  id: number;
  email: string;
  status: string;
  created_at: string;
}

interface AllowedEmail {
  id: number;
  email: string;
  created_at: string;
}

interface Invitation {
  id: number;
  email: string;
  token: string;
  created_at: string;
  expires_at: string | null;
  consumed_at: string | null;
  revoked_at: string | null;
}

interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
}

interface ActivityEntry {
  id: number;
  user_id: string | null;
  email: string;
  action: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

// 128-bit token with a Protein Pal-branded prefix and dashed grouping.
// Format: pp_inv_xxxxxxxx-xxxxxxxx-xxxxxxxx-xxxxxxxx (Stripe/GitHub style).
function generateInviteToken(): string {
  const g: any = typeof globalThis !== "undefined" ? (globalThis as any) : {};
  const cryptoObj = g.crypto;
  let hex = "";
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i].toString(16).padStart(2, "0");
    }
  } else {
    hex = (
      Math.random().toString(16).slice(2) +
      Math.random().toString(16).slice(2) +
      Date.now().toString(16)
    )
      .replace(/[^0-9a-f]/g, "")
      .padEnd(32, "0")
      .slice(0, 32);
  }
  // Group 32 hex chars into four 8-char chunks separated by dashes.
  return (
    "pp_inv_" +
    hex.slice(0, 8) +
    "-" +
    hex.slice(8, 16) +
    "-" +
    hex.slice(16, 24) +
    "-" +
    hex.slice(24, 32)
  );
}

type TabKey = "requests" | "whitelist" | "users" | "activity";

export default function AdminPanel() {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<SignupRequest[]>([]);
  const [whitelist, setWhitelist] = useState<AllowedEmail[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityEntry[]>([]);
  const [removedUsers, setRemovedUsers] = useState<Set<string>>(new Set());
  const [newEmail, setNewEmail] = useState("");
  const [activeTab, setActiveTab] = useState<TabKey>("requests");
  const [loadingAction, setLoadingAction] = useState<number | string | null>(
    null
  );
  const [message, setMessage] = useState("");
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(
    null
  );
  const [resetPasswordValue, setResetPasswordValue] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    // selectAll pages past PostgREST's silent 1000-row cap. It orders by id
    // ascending, so reverse to keep the previous newest-first display order.
    try {
      const [reqRows, wlRows, invRows] = await Promise.all([
        selectAll<SignupRequest>("signup_requests"),
        selectAll<AllowedEmail>("allowed_emails"),
        selectAll<Invitation>("invitations"),
      ]);
      setRequests(reqRows.reverse());
      setWhitelist(wlRows.reverse());
      setInvitations(invRows.reverse());
    } catch {
      // Keep whatever is already displayed; the old code ignored errors too.
    }
  }

  function activeInviteFor(email: string): Invitation | null {
    const now = Date.now();
    const candidates = invitations
      .filter(
        (inv) =>
          inv.email.toLowerCase() === email.toLowerCase() &&
          !inv.consumed_at &&
          !inv.revoked_at &&
          (!inv.expires_at || new Date(inv.expires_at).getTime() > now),
      )
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    return candidates[0] ?? null;
  }

  function consumedInviteFor(email: string): Invitation | null {
    return (
      invitations.find(
        (inv) => inv.email.toLowerCase() === email.toLowerCase() && inv.consumed_at,
      ) ?? null
    );
  }

  function inviteUrl(token: string): string {
    return `${window.location.origin}${window.location.pathname}?invite=${token}`;
  }

  async function generateInvite(email: string) {
    setMessage("");
    // Revoke any prior outstanding invitations for this email so only the
    // new link works.
    const prior = invitations.filter(
      (inv) =>
        inv.email.toLowerCase() === email.toLowerCase() &&
        !inv.consumed_at &&
        !inv.revoked_at,
    );
    if (prior.length > 0) {
      await supabase
        .from("invitations")
        .update({ revoked_at: new Date().toISOString() })
        .in(
          "id",
          prior.map((p) => p.id),
        );
    }

    const token = generateInviteToken();

    const { error: insErr } = await supabase.from("invitations").insert({
      email: email.toLowerCase(),
      token,
      invited_by: null,
    });
    if (insErr) {
      setMessage(t('admin.failedGenerateInvite', { error: insErr.message }));
      return;
    }
    setMessage(t('admin.inviteGenerated', { email }));
    loadData();
  }

  async function revokeInvite(inv: Invitation) {
    setMessage("");
    // .select() verifies a row was actually updated — under RLS a 0-row
    // PATCH returns 200 with no error, so error alone is not enough.
    const { data, error } = await supabase
      .from("invitations")
      .update({ revoked_at: new Date().toISOString() })
      .eq("id", inv.id)
      .select("id");
    if (error || !data || data.length === 0) {
      setMessage(t('admin.failedRevokeInvite', { email: inv.email, error: error ? `: ${error.message}` : "" }));
      return;
    }
    setMessage(t('admin.revokedInvite', { email: inv.email }));
    loadData();
  }

  async function copyInviteUrl(token: string) {
    const url = inviteUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      window.setTimeout(() => setCopiedToken(null), 1800);
    } catch {
      window.prompt("Copy this signup URL:", url);
    }
  }

  async function loadActivityLog() {
    const { data } = await supabase
      .from("activity_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(200);
    if (data) setActivityLog(data);
  }

  async function approveRequest(req: SignupRequest) {
    setLoadingAction(req.id);
    setMessage("");

    const { error: insertErr } = await supabase
      .from("allowed_emails")
      .insert({ email: req.email });

    if (insertErr && !insertErr.message?.includes("duplicate")) {
      setMessage(t('admin.failedAddWhitelist', { error: insertErr.message }));
      setLoadingAction(null);
      return;
    }

    // .select() verifies a row was actually updated — under RLS a 0-row
    // PATCH returns 200 with no error, so error alone is not enough.
    const { data: approvedRows, error: approveErr } = await supabase
      .from("signup_requests")
      .update({ status: "approved", reviewed_at: new Date().toISOString() })
      .eq("id", req.id)
      .select("id");

    if (approveErr || !approvedRows || approvedRows.length === 0) {
      setMessage(t('admin.failed', { error: approveErr?.message ?? "no row updated" }));
      setLoadingAction(null);
      return;
    }

    setLoadingAction(null);
    setMessage(t('admin.approved', { email: req.email }));
    loadData();
  }

  async function denyRequest(req: SignupRequest) {
    setLoadingAction(req.id);
    setMessage("");

    // .select() verifies a row was actually updated — under RLS a 0-row
    // PATCH returns 200 with no error, so error alone is not enough.
    const { data: deniedRows, error: denyErr } = await supabase
      .from("signup_requests")
      .update({ status: "denied", reviewed_at: new Date().toISOString() })
      .eq("id", req.id)
      .select("id");

    if (denyErr || !deniedRows || deniedRows.length === 0) {
      setMessage(t('admin.failed', { error: denyErr?.message ?? "no row updated" }));
      setLoadingAction(null);
      return;
    }

    setLoadingAction(null);
    setMessage(t('admin.denied', { email: req.email }));
    loadData();
  }

  async function addToWhitelist() {
    const trimmed = newEmail.toLowerCase().trim();
    if (!trimmed) return;
    setMessage("");

    const { error } = await supabase
      .from("allowed_emails")
      .insert({ email: trimmed });

    if (error) {
      setMessage(
        error.message?.includes("duplicate")
          ? t('admin.emailAlreadyWhitelisted')
          : t('admin.failed', { error: error.message })
      );
      return;
    }

    setNewEmail("");
    setMessage(t('admin.addedToWhitelist', { email: trimmed }));
    loadData();
  }

  async function removeFromWhitelist(entry: AllowedEmail) {
    setLoadingAction(entry.id);
    setMessage("");

    // .select() verifies a row was actually deleted — under RLS a 0-row
    // DELETE returns 200 with no error, so error alone is not enough.
    const { data: deletedRows, error: deleteErr } = await supabase
      .from("allowed_emails")
      .delete()
      .eq("id", entry.id)
      .select("id");

    if (deleteErr || !deletedRows || deletedRows.length === 0) {
      setMessage(t('admin.failed', { error: deleteErr?.message ?? "no row deleted" }));
      setLoadingAction(null);
      return;
    }

    setLoadingAction(null);
    setMessage(t('admin.removedEmail', { email: entry.email }));
    loadData();
  }

  async function getAuthToken(): Promise<string> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? "";
  }

  async function loadUsers() {
    try {
      const token = await getAuthToken();
      const resp = await fetch("/api/admin/users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setUsers(data.users);
      } else {
        const text = await resp.text();
        let detail: string;
        try {
          detail = JSON.parse(text).error;
        } catch {
          detail = `${resp.status} ${resp.statusText}`;
        }
        setMessage(t('admin.failedLoadUsers', { error: detail }));
      }
    } catch (err: any) {
      setMessage(t('admin.failedLoadUsers', { error: err?.message || t('admin.networkError') }));
    }
  }

  async function removeUser(user: AuthUser) {
    if (
      !window.confirm(
        t('admin.confirmRemoveUser', { email: user.email })
      )
    )
      return;

    setLoadingAction(user.id);
    setMessage("");
    const token = await getAuthToken();
    const resp = await fetch("/api/admin/users", {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId: user.id }),
    });

    setLoadingAction(null);
    if (resp.ok) {
      setRemovedUsers((prev) => new Set(prev).add(user.id));
      setMessage(t('admin.removedEmail', { email: user.email }));
    } else {
      const data = await resp.json();
      setMessage(t('admin.failedRemove', { error: data.error }));
    }
  }

  async function resetUserPassword(userId: string) {
    if (!resetPasswordValue || resetPasswordValue.length < 6) {
      setMessage(t('admin.passwordMinLength'));
      return;
    }

    setLoadingAction(userId);
    setMessage("");
    const token = await getAuthToken();
    const resp = await fetch("/api/admin/users", {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId, password: resetPasswordValue }),
    });

    setLoadingAction(null);
    if (resp.ok) {
      setMessage(t('admin.passwordUpdatedSuccess'));
      setResetPasswordUserId(null);
      setResetPasswordValue("");
    } else {
      const data = await resp.json();
      setMessage(t('admin.failedResetPassword', { error: data.error }));
    }
  }

  const pendingRequests = requests.filter((r) => r.status === "pending");
  const resolvedRequests = requests.filter((r) => r.status !== "pending");

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 16px",
    borderRadius: "6px",
    border: "none",
    background: active ? "rgba(139,92,246,0.3)" : "transparent",
    color: active ? "#f8fafc" : "#94a3b8",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  const cardStyle: React.CSSProperties = {
    background: "#0f0b2a",
    border: "1px solid rgba(139,92,246,0.15)",
    borderRadius: "10px",
    padding: "14px 16px",
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
  };

  const btnStyle = (
    color: string,
    disabled: boolean
  ): React.CSSProperties => ({
    padding: "6px 14px",
    borderRadius: "6px",
    border: "none",
    background: color,
    color: "white",
    fontSize: "13px",
    fontWeight: 500,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0618",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: "40px 24px",
      }}
    >
      <div style={{ maxWidth: "780px", margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <h1
            style={{
              color: "#f8fafc",
              fontSize: "24px",
              fontWeight: 700,
              margin: 0,
            }}
          >
            {t('admin.adminPanel')}
          </h1>
        </div>

        {/* Status message */}
        {message && (
          <div
            style={{
              background: "rgba(139,92,246,0.15)",
              border: "1px solid rgba(139,92,246,0.3)",
              borderRadius: "8px",
              padding: "10px 16px",
              marginBottom: "20px",
              color: "#c4b5fd",
              fontSize: "13px",
            }}
          >
            {message}
          </div>
        )}

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            marginBottom: "24px",
            background: "rgba(15,11,42,0.8)",
            borderRadius: "8px",
            padding: "4px",
            border: "1px solid rgba(139,92,246,0.15)",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => setActiveTab("requests")}
            style={tabStyle(activeTab === "requests")}
          >
            {t('admin.signups')}
            {pendingRequests.length > 0 && (
              <span
                style={{
                  marginLeft: "6px",
                  background: "#dc2626",
                  color: "white",
                  borderRadius: "10px",
                  padding: "1px 7px",
                  fontSize: "11px",
                }}
              >
                {pendingRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("whitelist")}
            style={tabStyle(activeTab === "whitelist")}
          >
            {t('admin.whitelist')} ({whitelist.length})
          </button>
          <button
            onClick={() => {
              setActiveTab("users");
              loadUsers();
            }}
            style={tabStyle(activeTab === "users")}
          >
            {t('admin.users')}
          </button>
          <button
            onClick={() => {
              setActiveTab("activity");
              loadActivityLog();
            }}
            style={tabStyle(activeTab === "activity")}
          >
            {t('admin.activity')}
          </button>
        </div>

        {/* ── Signup Requests Tab ── */}
        {activeTab === "requests" && (
          <div>
            {pendingRequests.length > 0 && (
              <>
                <h3
                  style={{
                    color: "#fbbf24",
                    fontSize: "14px",
                    fontWeight: 600,
                    margin: "0 0 12px 0",
                  }}
                >
                  {t('admin.pending')} ({pendingRequests.length})
                </h3>
                {pendingRequests.map((req) => (
                  <div key={req.id} style={cardStyle}>
                    <div>
                      <div
                        style={{
                          color: "#f8fafc",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {req.email}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        {new Date(req.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => approveRequest(req)}
                        disabled={loadingAction === req.id}
                        style={btnStyle("#16a34a", loadingAction === req.id)}
                      >
                        {t('admin.approve')}
                      </button>
                      <button
                        onClick={() => denyRequest(req)}
                        disabled={loadingAction === req.id}
                        style={btnStyle("#dc2626", loadingAction === req.id)}
                      >
                        {t('admin.deny')}
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {pendingRequests.length === 0 && (
              <p
                style={{
                  color: "#64748b",
                  fontSize: "14px",
                  textAlign: "center",
                  padding: "32px 0",
                }}
              >
                {t('admin.noPendingRequests')}
              </p>
            )}

            {resolvedRequests.length > 0 && (
              <>
                <h3
                  style={{
                    color: "#94a3b8",
                    fontSize: "14px",
                    fontWeight: 600,
                    margin: "24px 0 12px 0",
                  }}
                >
                  {t('admin.resolved')} ({resolvedRequests.length})
                </h3>
                {resolvedRequests.map((req) => (
                  <div key={req.id} style={cardStyle}>
                    <div>
                      <div
                        style={{
                          color: "#94a3b8",
                          fontSize: "14px",
                        }}
                      >
                        {req.email}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        {new Date(req.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 500,
                        background:
                          req.status === "approved"
                            ? "rgba(22,163,74,0.2)"
                            : "rgba(220,38,38,0.2)",
                        color:
                          req.status === "approved" ? "#4ade80" : "#fca5a5",
                      }}
                    >
                      {req.status}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Whitelist Tab ── */}
        {activeTab === "whitelist" && (
          <div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "20px",
              }}
            >
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('admin.emailPlaceholder')}
                onKeyDown={(e) => e.key === "Enter" && addToWhitelist()}
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  borderRadius: "8px",
                  border: "1px solid rgba(139,92,246,0.3)",
                  background: "#0f0b2a",
                  color: "#f8fafc",
                  fontSize: "14px",
                  outline: "none",
                }}
              />
              <button
                onClick={addToWhitelist}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background:
                    "linear-gradient(135deg, #8b5cf6, #a78bfa)",
                  color: "white",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {t('admin.addEmail')}
              </button>
            </div>

            {whitelist.map((entry) => {
              const active = activeInviteFor(entry.email);
              const consumed = consumedInviteFor(entry.email);
              return (
                <div
                  key={entry.id}
                  style={{
                    ...cardStyle,
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#f8fafc",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {entry.email}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        {t('admin.addedDate', { date: new Date(entry.created_at).toLocaleDateString() })}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {!active && !consumed && (
                        <button
                          onClick={() => generateInvite(entry.email)}
                          style={btnStyle("#7c3aed", false)}
                        >
                          {t('admin.generateSignupLink')}
                        </button>
                      )}
                      {!active && consumed && (
                        <button
                          onClick={() => generateInvite(entry.email)}
                          style={btnStyle("#475569", false)}
                          title="Account already created from a prior link"
                        >
                          {t('admin.newLink')}
                        </button>
                      )}
                      <button
                        onClick={() => removeFromWhitelist(entry)}
                        disabled={loadingAction === entry.id}
                        style={btnStyle("#dc2626", loadingAction === entry.id)}
                      >
                        {t('admin.remove')}
                      </button>
                    </div>
                  </div>

                  {active && (
                    <div
                      style={{
                        marginTop: "10px",
                        background: "rgba(124,58,237,0.1)",
                        border: "1px solid rgba(124,58,237,0.3)",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                      }}
                    >
                      <code
                        style={{
                          flex: 1,
                          color: "#c4b5fd",
                          fontSize: "12px",
                          fontFamily: "monospace",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          minWidth: 0,
                        }}
                        title={inviteUrl(active.token)}
                      >
                        {inviteUrl(active.token)}
                      </code>
                      <span
                        style={{
                          color: "#94a3b8",
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {active.expires_at
                          ? t('admin.expiresDate', { date: new Date(active.expires_at).toLocaleDateString() })
                          : t('admin.noExpiration')}
                      </span>
                      <button
                        onClick={() => copyInviteUrl(active.token)}
                        style={btnStyle(
                          copiedToken === active.token ? "#16a34a" : "#6366f1",
                          false,
                        )}
                      >
                        {copiedToken === active.token ? t('admin.copied') : t('admin.copy')}
                      </button>
                      <button
                        onClick={() => revokeInvite(active)}
                        style={btnStyle("rgba(220,38,38,0.7)", false)}
                      >
                        {t('admin.revoke')}
                      </button>
                    </div>
                  )}

                  {!active && consumed && (
                    <div
                      style={{
                        marginTop: "8px",
                        color: "#64748b",
                        fontSize: "11px",
                      }}
                    >
                      {t('admin.accountActivated', { date: new Date(consumed.consumed_at!).toLocaleDateString() })}
                    </div>
                  )}
                </div>
              );
            })}

            {whitelist.length === 0 && (
              <p
                style={{
                  color: "#64748b",
                  fontSize: "14px",
                  textAlign: "center",
                  padding: "32px 0",
                }}
              >
                {t('admin.noWhitelistedEmails')}
              </p>
            )}
          </div>
        )}

        {/* ── Users Tab ── */}
        {activeTab === "users" && (
          <div>
            {users.length === 0 ? (
              <p
                style={{
                  color: "#64748b",
                  fontSize: "14px",
                  textAlign: "center",
                  padding: "32px 0",
                }}
              >
                {t('admin.loadingUsers')}
              </p>
            ) : (
              users.map((user) => {
                const isRemoved = removedUsers.has(user.id);
                return (
                  <div
                    key={user.id}
                    style={{
                      ...cardStyle,
                      opacity: isRemoved ? 0.5 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          color: isRemoved ? "#94a3b8" : "#f8fafc",
                          fontSize: "14px",
                          fontWeight: 500,
                          textDecoration: isRemoved
                            ? "line-through"
                            : "none",
                        }}
                      >
                        {user.email}
                      </div>
                      <div style={{ color: "#64748b", fontSize: "12px" }}>
                        {t('admin.joinedDate', { date: new Date(user.created_at).toLocaleDateString() })}
                        {user.last_sign_in_at &&
                          t('admin.lastLoginDate', { date: new Date(user.last_sign_in_at).toLocaleDateString() })}
                        {!user.email_confirmed_at && (
                          <span
                            style={{ color: "#fbbf24", marginLeft: "8px" }}
                          >
                            {t('admin.unconfirmed')}
                          </span>
                        )}
                        {isRemoved && (
                          <span
                            style={{ color: "#ef4444", marginLeft: "8px" }}
                          >
                            {t('admin.removed')}
                          </span>
                        )}
                      </div>

                      {/* Inline password reset form */}
                      {resetPasswordUserId === user.id && (
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            marginTop: "10px",
                          }}
                        >
                          <input
                            type="password"
                            value={resetPasswordValue}
                            onChange={(e) =>
                              setResetPasswordValue(e.target.value)
                            }
                            placeholder={t('admin.newPasswordMin')}
                            style={{
                              flex: 1,
                              padding: "6px 10px",
                              borderRadius: "6px",
                              border: "1px solid rgba(139,92,246,0.3)",
                              background: "rgba(15,11,42,0.8)",
                              color: "#f8fafc",
                              fontSize: "13px",
                              outline: "none",
                            }}
                          />
                          <button
                            onClick={() => resetUserPassword(user.id)}
                            disabled={loadingAction === user.id}
                            style={btnStyle(
                              "#8b5cf6",
                              loadingAction === user.id
                            )}
                          >
                            {t('admin.save')}
                          </button>
                          <button
                            onClick={() => {
                              setResetPasswordUserId(null);
                              setResetPasswordValue("");
                            }}
                            style={btnStyle("rgba(100,116,139,0.5)", false)}
                          >
                            {t('admin.cancel')}
                          </button>
                        </div>
                      )}
                    </div>
                    {!isRemoved && (
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexShrink: 0,
                        }}
                      >
                        <button
                          onClick={() => {
                            setResetPasswordUserId(
                              resetPasswordUserId === user.id
                                ? null
                                : user.id
                            );
                            setResetPasswordValue("");
                          }}
                          disabled={loadingAction === user.id}
                          style={btnStyle(
                            "#6366f1",
                            loadingAction === user.id
                          )}
                        >
                          {t('admin.resetPW')}
                        </button>
                        <button
                          onClick={() => removeUser(user)}
                          disabled={loadingAction === user.id}
                          style={btnStyle(
                            "#dc2626",
                            loadingAction === user.id
                          )}
                        >
                          {t('admin.removeUser')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── Activity Log Tab ── */}
        {activeTab === "activity" && (
          <div>
            {activityLog.length === 0 ? (
              <p
                style={{
                  color: "#64748b",
                  fontSize: "14px",
                  textAlign: "center",
                  padding: "32px 0",
                }}
              >
                {t('admin.noActivityLogged')}
              </p>
            ) : (
              <div
                style={{
                  overflowX: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr>
                      {[t('admin.time'), t('admin.email'), t('admin.action'), t('admin.details')].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "8px 12px",
                            color: "#94a3b8",
                            fontWeight: 600,
                            borderBottom:
                              "1px solid rgba(139,92,246,0.15)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activityLog.map((entry) => (
                      <tr key={entry.id}>
                        <td
                          style={{
                            padding: "8px 12px",
                            color: "#64748b",
                            borderBottom:
                              "1px solid rgba(139,92,246,0.08)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {new Date(entry.created_at).toLocaleString()}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            color: "#f8fafc",
                            borderBottom:
                              "1px solid rgba(139,92,246,0.08)",
                          }}
                        >
                          {entry.email}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            borderBottom:
                              "1px solid rgba(139,92,246,0.08)",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background:
                                entry.action === "login_success"
                                  ? "rgba(22,163,74,0.2)"
                                  : entry.action === "login_failed"
                                    ? "rgba(220,38,38,0.2)"
                                    : entry.action === "sign_out"
                                      ? "rgba(100,116,139,0.2)"
                                      : "rgba(139,92,246,0.2)",
                              color:
                                entry.action === "login_success"
                                  ? "#4ade80"
                                  : entry.action === "login_failed"
                                    ? "#fca5a5"
                                    : entry.action === "sign_out"
                                      ? "#94a3b8"
                                      : "#c4b5fd",
                            }}
                          >
                            {entry.action}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            color: "#64748b",
                            borderBottom:
                              "1px solid rgba(139,92,246,0.08)",
                            fontSize: "11px",
                          }}
                        >
                          {entry.metadata
                            ? JSON.stringify(entry.metadata)
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
